import { each, filter, isNumber, keys } from "lodash";

export type FileMeta = { [metaKey: string]: any };

export interface FileUploadItem
{
    file: File;
    meta: FileMeta;
}

export interface FileUploadData
{
    [inputName: string]: {
        items: FileUploadItem[];
        uploadData: any;
        progressTitle?: string;
    };
}

export interface UploadProgressData
{
	fileIndex: number,
	totalFiles: number,
	file: File|undefined,
	fileMeta: FileMeta|undefined;
	fileProgress: number, // 0 <= n <= 1
	totalProgress: number, // 0 <= n <= 1
	totalBytesProgress: number,
	fileBytesProgress: number,
	totalBytes: number,
}

export interface UploadResult
{
	ok: boolean;
	serverData: any;
	files: FileUploadItem[];
	progress: UploadProgressData;
	statusCode: number;
	xhrEvent?: any;
}

export type UploadProgressHandler = (data: UploadProgressData) => void;

export class FileUploadItemError extends Error
{
    constructor(consoleMsg: string, userMsg: string) {
        super(consoleMsg);
        errorModal(userMsg);
    }
}

export interface SingleUploadResult {
    id: number;
    url: string;
    title: string;
}

declare const jQuery: any;
const $ = jQuery;

const fileUpload: Function = window["iw"].fileUpload;
const errorModal: Function = window["iw"].modal.errorModal;
const progressModal: Function = window["iw"].modal.progressModal;

export type InputType = "images"|"documents";

export interface InputNameConfigMap {
    [inputName: string]: {
        progressMsg: string;
        inputType: InputType;
    };
}

// handlerName - NAME const on PHP FileUpload class
// rawData - as returned by gatherData()
// inputNameConfigMap - keys (input names) determine which rawData data is uploaded
// uploadData - data to be sent along with every file upload
export async function uploadAttachments(
    handlerName: string,
    rawData,
    inputNameConfigMap: InputNameConfigMap,
    uploadData: any
): Promise<void> {
    const inputNames: string[] = keys(inputNameConfigMap);
    const totalNumFiles: number = countAttachmentUploads(rawData, inputNames);
    const getProgressMsg = (inputName: string): string => inputNameConfigMap[inputName]?.progressMsg || `Uploading files...`;
    const $modal = progressModal(getProgressMsg(""), `Uploading <span data-progress-num="">1</span> of ${totalNumFiles}`);
    const $bar = $modal.find(`progress`).val(0);
    const $num = $modal.find(`[data-progress-num]`);
    const $title = $modal.find(`.modal-title`);
    let numComplete = 0;
    const onFileUploaded = (): void => {
        ++numComplete;
        const totalProgress: number = numComplete / totalNumFiles;
        $bar.val(totalProgress);
        $num.html((numComplete + 1).toString());
    };
    for (const inputName of inputNames)
    {
        const inputType: InputType = inputNameConfigMap[inputName].inputType;
        $title.text(getProgressMsg(inputName));
        await uploadInputAttachments(handlerName, rawData, inputName, uploadData, onFileUploaded, inputType);
    }
}

// Mutates rawData array such that new items are converted to saved items
export async function uploadInputAttachments(
    handlerName: string, // PHP FileUpload NAME const
    rawData, // as returned by gatherData()
    inputName: string, // must be key in rawData
    uploadData: any,
    onFileUploaded: () => void,
    inputType: InputType
): Promise<void> {
    const newItems: any[] = rawData[inputName]?.new;
    const savedItems: any[] = rawData[inputName]?.saved;
    if (newItems?.length === undefined || savedItems?.length === undefined)
    {
        console.warn(`No MII 'new'/'saved' array found for ${inputName}`);
        return;
    }
    const num: number = newItems.length;
    for (let i = 0; i < num; ++i)
    {
        const newItem = newItems[i];
        if (!(newItem.file instanceof File))
        {
            console.error(`uploadInputAttachments - newItem has no file:`, newItem);
            return undefined;
        }
        const fileName: string = newItem.file.name;
        const newItemTitle: string = getNewItemTitle(newItem, inputType);
        try {
            const result: SingleUploadResult = await uploadSingleFile(handlerName, uploadData, newItem.file, newItemTitle);
            const newSavedItem = singleUploadResultToSavedItem(result, inputType, newItemTitle, newItem);
            savedItems.push(newSavedItem);
            onFileUploaded();
        } catch (error) {
            console.error("Error uploading item:", newItem, error);
            throw new FileUploadItemError(
                `Failed to upload file ${fileName} due to Error: "` + (error?.message || ""),
                `Failed to upload file "${fileName}" - please try using another file.`);
        }
    }
    if (inputType === "documents")
    {
        await uploadDocumentThumbs(savedItems, handlerName, uploadData, onFileUploaded);
    }
}

// Mutates the objects inside items array
async function uploadDocumentThumbs(
    items: any[],
    handlerName: string,
    uploadData: any,
    onFileUploaded: () => void
): Promise<void> {
    const num: number = items.length;
    for (let i = 0; i < num; ++i)
    {
        const item = items[i];
        const newImage = item.newImage;
        if (!newImage)
        {
            continue;
        }
        const file: File = newImage.file;
        if (!(file instanceof File))
        {
            throw new Error(`newImage.file is not a File`);
        }
        const fileName: string = file.name;
        try {
            const result: SingleUploadResult = await uploadSingleFile(handlerName, uploadData, newImage.file, "");
            item.newImage = null;
            item.savedImage = {
                id: result.id,
                deleted: false
            };
            onFileUploaded();
        } catch (error) {
            console.error("Error uploading document newImage:", newImage, error);
            throw new FileUploadItemError(
                `Failed to upload document thumbnail file ${fileName} due to Error: "` + (error?.message || ""),
                `Failed to upload document thumbnail "${fileName}" - please try using another file.`);
        }
    }
}

function singleUploadResultToSavedItem(result: SingleUploadResult, inputType: InputType, newTitle: string, origNewItem)
{
    const savedItem: any = {
        id: result.id,
        url: result.url,
        deleted: false,
    };
    const title: string = newTitle ?? result.title;
    switch (inputType)
    {
        case "images":
            savedItem.meta = { title };
            break;
        case "documents":
            savedItem.title = title;
            savedItem.newImage = origNewItem.newImage;
            break;
        default:
            throw new Error(`Bad inputType: ${inputType}`);
    }
    return savedItem;
}

async function uploadSingleFile(handlerName: string, uploadData, newItemFile: File, newItemTitle: string): Promise<SingleUploadResult> {
    const fileName: string = newItemFile.name;
    const fileUploadItem: FileUploadItem = {
        file: newItemFile,
        meta: {
            title: newItemTitle
        }
    };
    const result: UploadResult = await fileUpload([fileUploadItem], handlerName, uploadData);
    const serverData = result.serverData;
    if (!serverData) throw new FileUploadItemError(
        `No serverData returned from upload of file ${fileName}`,
        `Failed to upload file "${fileName}" - please try using another file.`);
    if (!serverData.id || !isNumber(serverData.id)) throw new FileUploadItemError(
        `No (number) serverData.id returned from upload of file ${fileName}`,
        `Failed to upload file "${fileName}" - please try using another file.`);
    return {
        id: serverData.id,
        url: serverData.url,
        title: newItemTitle || serverData.title
    };
}

function countAttachmentUploads(rawData, inputNames: string[]): number {
    let count = 0;
    each(inputNames, (inputName: string): void => {
        const items = filter(rawData[inputName]?.new || [], (newItem) => (newItem.file instanceof File));
        count += items.length;
        each(items, item => {
            if (item.newImage) {
                ++count;
            }
        });
        each(rawData[inputName]?.saved || [], (savedItem) => {
            if (savedItem.newImage && !savedItem.deleted) {
                ++count;
            }
        });
    });
    return count;
}

function getNewItemTitle(newItem, inputType: InputType): string
{
    switch (inputType)
    {
        case "images":
            return newItem.meta?.title || "";
        case "documents":
            return newItem.title || "";
        default:
            throw new Error(`Bad inputType: ${inputType}`);
    }
}
